import { optionPlanKind } from '@erp/shared-types';
import type {
  GetSuggestionDetailResponse,
  SuggestionOption,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { overrideActionsFor, type OverrideAction } from './overrideActions';
import type { OverrideFormDraft } from './overrideFormDraft';
import type {
  OverrideSectionSource,
  OverrideSectionVendor,
} from './overrideSectionTypes';

function isOverrideActionFromOption(v: string): v is OverrideAction {
  return v === 'buy' || v === 'transfer' || v === 'buy_and_transfer';
}

/**
 * The option the override form starts from. An override buys or transfers the
 * suggestion's own product, so a substitute or make plan's supplier or source
 * would pair it with the wrong product; only direct options qualify.
 */
export function resolveCurrentOverrideOption(
  suggestion: Pick<GetSuggestionDetailResponse, 'options' | 'chosenOptionId'>,
): SuggestionOption | null {
  const direct = suggestion.options.filter(
    (o) => optionPlanKind(o.legs.map((leg) => leg.legType)) === 'direct',
  );
  const chosen = direct.find((o) => o.optionId === suggestion.chosenOptionId);
  if (chosen) return chosen;
  return [...direct].sort((a, b) => a.rank - b.rank)[0] ?? null;
}

export function mergeOverrideTransferSources(
  rawSources: OverrideSectionSource[],
  currentOption: ReturnType<typeof resolveCurrentOverrideOption>,
): OverrideSectionSource[] {
  const optSrcId = currentOption?.sourceWarehouseId ?? null;
  const optSrcName = currentOption?.sourceWarehouseName ?? null;
  if (optSrcId && !rawSources.some((s) => s.warehouseId === optSrcId)) {
    return [
      {
        warehouseId: optSrcId,
        warehouseName: optSrcName ?? 'Current source',
        warehouseCode: null,
        divisionName: null,
        divisionCode: null,
        onHandQuantity: 0,
      },
      ...rawSources,
    ];
  }
  return rawSources;
}

export type OverrideEffectiveState = {
  effectiveAction: OverrideAction;
  actionChoices: OverrideAction[];
  effectiveVendorId: string;
  effectiveSourceId: string;
  selectedVendor: OverrideSectionVendor | undefined;
  isBuy: boolean;
  isTransfer: boolean;
  qty: number;
  applyQuantityToAllLines: boolean;
};

function defaultOverrideAction(
  side: 'buy' | 'transfer' | undefined,
  currentOption: ReturnType<typeof resolveCurrentOverrideOption>,
): OverrideAction {
  if (side != null) return side;
  const fromOption = currentOption?.action;
  if (fromOption != null && isOverrideActionFromOption(fromOption)) {
    return fromOption;
  }
  return 'buy';
}

export function deriveOverrideEffectiveState({
  side,
  formDraft,
  currentOption,
  sources,
  vendors,
  bulkLineCount,
}: {
  side?: 'buy' | 'transfer';
  formDraft: OverrideFormDraft;
  currentOption: ReturnType<typeof resolveCurrentOverrideOption>;
  sources: OverrideSectionSource[];
  vendors: OverrideSectionVendor[];
  bulkLineCount: number;
}): OverrideEffectiveState {
  const { quantity, quantityAtOpen, action, vendorId, sourceId } = formDraft;
  const effectiveAction: OverrideAction =
    action ?? defaultOverrideAction(side, currentOption);
  const effectiveVendorId =
    vendorId || (currentOption?.supplierId ?? vendors[0]?.supplierId ?? '');
  const effectiveSourceId =
    sourceId ||
    (currentOption?.sourceWarehouseId ?? sources[0]?.warehouseId ?? '');
  const selectedVendor = vendors.find(
    (v) => v.supplierId === effectiveVendorId,
  );
  const isBuy =
    effectiveAction === 'buy' || effectiveAction === 'buy_and_transfer';
  const isTransfer =
    effectiveAction === 'transfer' || effectiveAction === 'buy_and_transfer';
  const qty = Number(quantity);

  return {
    effectiveAction,
    actionChoices: overrideActionsFor(side),
    effectiveVendorId,
    effectiveSourceId,
    selectedVendor,
    isBuy,
    isTransfer,
    qty,
    applyQuantityToAllLines: bulkLineCount <= 1 || quantity !== quantityAtOpen,
  };
}

export function overrideFormCanSubmit(
  effective: Pick<
    OverrideEffectiveState,
    'isBuy' | 'isTransfer' | 'qty' | 'effectiveVendorId' | 'effectiveSourceId'
  >,
  pending: boolean,
  candidatesLoading: boolean,
): boolean {
  if (pending || candidatesLoading) return false;
  if (effective.qty <= 0) return false;
  if (effective.isBuy && !effective.effectiveVendorId) return false;
  if (effective.isTransfer && !effective.effectiveSourceId) return false;
  return true;
}
